import {emotionStyled, tokens} from '@packages/utilities/styled';

import {FloatingArrow} from '@floating-ui/react';

import {HStack} from '../Stack/HStack';
import {Text} from '../Text';
import {View} from '../View';

export const PopoverWrapperView = emotionStyled('div')(
  {
    background: '$background1',
    boxShadow: '$menu',
    color: '$actionPrimaryText',
    fontSize: '$body',
    fontWeight: '$normal',
    borderRadius: '$2',
    transitionProperty: 'opacity,transform',
    display: 'flex', // used to contain children content if they extend beyond the Wrapper's boundaries.
    ':focus-visible': {
      outline: 'none',
    },
  },
  {
    componentName: 'PopoverContent',
    variants: {
      colorVariant: {
        default: {
          backgroundColor: '$background1',
          color: '$textPrimary',
          '.fill': {
            fill: '$background3',
          },
        },
        secondary: {
          backgroundColor: '$background2',
          color: '$textPrimary',
          '.fill': {
            fill: '$background3',
          },
        },
        tertiary: {
          backgroundColor: '$background3',
          color: '$textPrimary',
          '.fill': {
            fill: '$background3',
          },
        },
        blue: {
          backgroundColor: '$actionPrimaryBackground',
          '.fill': {
            fill: '$actionPrimaryBackground',
          },
        },
      },
      hasSoftCorners: {
        true: {
          borderRadius: '$6',
        },
      },
      lightModeShadowPlacement: {
        top: {
          filter: `
          drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.04))
          drop-shadow(0px 2px 1px rgba(0, 0, 0, 0.02))
        `,
        },
        bottom: {
          /* The rgba is slightly darker here because our light-source is "above" us*/
          filter: `
            drop-shadow(0px -1px 0px rgba(0, 0, 0, 0.08))
            drop-shadow(0px -2px 1px rgba(0, 0, 0, 0.02))
          `,
        },
        left: {
          filter: `
          drop-shadow(1px 0px 0px rgba(0, 0, 0, 0.04))
          drop-shadow(1px 2px 1px rgba(0, 0, 0, 0.02))
        `,
        },
        right: {
          filter: `
          drop-shadow(-1px 0px 0px rgba(0, 0, 0, 0.04))
          drop-shadow(-1px 2px 1px rgba(0, 0, 0, 0.02))
        `,
        },
      },
    },
    defaultVariants: {
      color: 'default',
    },
  }
);

export const PopoverHeaderView = emotionStyled(HStack)(
  {
    padding: tokens.ui.popover.header.padding,
    width: '100%',
  },
  {
    componentName: 'PopoverHeader',
    variants: {
      hasBorder: {
        true: {
          borderBottom: '$border1',
        },
      },
    },
  }
);

export const PopoverTitleView = emotionStyled(Text)(
  {
    width: 'fit-content',
  },
  {
    componentName: 'PopoverTitle',
  }
);

export const PopoverHeaderContentView = emotionStyled(HStack)(
  {
    gap: tokens.ui.popover.header.spacing,
    padding: tokens.ui.popover.headerContent.padding,
    // minWidth needed to allow for shrinking/truncating
    minWidth: 0,
  },
  {
    componentName: 'PopoverHeaderContent',
  }
);

export const PopoverHeaderEmptyIconButtonView = emotionStyled('div')(
  {
    height: tokens.ui.popover.header.iconButtonHeight,
    width: 0,
  },
  {
    componentName: 'PopoverHeaderEmptyIconButton',
  }
);

export const PopoverBodyView = emotionStyled(View)(
  {
    overflowY: 'auto',
  },
  {
    componentName: 'PopoverBody',
  }
);

export const PopoverFooterView = emotionStyled(HStack)(
  {
    borderTop: '$border1',
    gap: tokens.ui.popover.footer.spacing,
    justifyContent: 'flex-end',
    padding: tokens.ui.popover.footer.padding,
  },
  {
    componentName: 'PopoverFooter',
  }
);

export const PopoverContentView = emotionStyled('div')(
  {
    borderRadius: '$2',
    overflowY: 'auto',
    padding: '$2',
    width: '100%',
    // NOTE: August 5, 2024: This is important to only display flex when PopoverBody is used
    // so that we don't cause visual regressions on any existing Popovers that don't use
    // PopoverHeader, PopoverBody, and PopoverFooter.
    [`&:has(${PopoverBodyView})`]: {
      display: 'flex',
      flex: 'auto',
      flexDirection: 'column',
    },
    [`&:has(${PopoverHeaderView}), &:has(${PopoverBodyView}), &:has(${PopoverFooterView})`]:
      {
        padding: 0,
      },
  },
  {
    variants: {
      hasSoftCorners: {
        true: {
          borderRadius: '$6',
        },
      },
      isSeamless: {
        true: {
          '&&': {
            padding: 0,
          },
        },
      },
    },
  }
);

export const PopoverArrowView = emotionStyled(FloatingArrow)(
  {
    fill: '$background1',
  },
  {
    componentName: 'PopoverArrow',
    variants: {
      colorVariant: {
        default: {
          fill: '$background1',
        },
        secondary: {
          fill: '$background2',
        },
        tertiary: {
          fill: '$background3',
        },
        blue: {
          fill: '$actionPrimaryBackground',
        },
      },
    },
    defaultVariants: {
      color: 'default',
    },
  }
);

export const PopoverArrowShadow = emotionStyled(PopoverArrowView)(
  {
    filter: `drop-shadow(0px 12px 24px rgba(0, 0, 0, 0.08))
      drop-shadow(0px 8px 16px rgba(0, 0, 0, 0.08))
      drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.08))
      drop-shadow(0px 2px 6px rgba(0, 0, 0, 0.08))
      drop-shadow(0px -0.5px 0.5px rgba(0, 0, 0, 0.12))
      drop-shadow(0px 0.5px 0.5px rgba(255, 255, 255, 0.12))`,
  },
  {
    componentName: 'PopoverArrowShadow',
    variants: {
      placement: {
        top: {
          filter: `drop-shadow(0px 12px 24px rgba(0, 0, 0, 0.08))
            drop-shadow(0px 8px 16px rgba(0, 0, 0, 0.08))
            drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.08))
            drop-shadow(0px 2px 6px rgba(0, 0, 0, 0.08))`,
        },
        bottom: {},
        left: {
          filter: `
            drop-shadow(1px 0.5px 0.5px rgba(255, 255, 255, 0.12))`,
        },
        right: {
          filter: `
          drop-shadow(-1px 0.5px 0.5px rgba(255, 255, 255, 0.12))`,
        },
      },
    },
  }
);
